import axios from 'axios';

let cacheBusterSeq = 0;
// Read GETs must never be served from a cached URL: presence (isonline) is
// recomputed server-side on every request, so a cached response silently
// freezes the roster's online dots. Unknown params are ignored by Moodle WS.
const withCacheBuster = (url) => `${url}&_=${Date.now()}-${++cacheBusterSeq}`;

export const checkPluginStatus = async (token, baseURL, courseId = 0) => {
  try {
    let url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_available_contacts&moodlewsrestformat=json`;
    if (courseId > 0) {
      url += `&courseid=${courseId}`;
    }
    const response = await axios.get(withCacheBuster(url));

    if (response.data && response.data.errorcode) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking Tipddy chat plugin status:', error);
    return false;
  }
};

export const getConversations = async (token, baseURL, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_conversations&courseid=${courseId}&moodlewsrestformat=json`;
    const response = await axios.get(withCacheBuster(url));
    
    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }
    
    return response.data.conversations || [];
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

export const getAvailableContacts = async (token, baseURL, courseId = 0) => {
  try {
    let url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_available_contacts&moodlewsrestformat=json`;
    if (courseId > 0) {
      url += `&courseid=${courseId}`;
    }
    const response = await axios.get(withCacheBuster(url));

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data.contacts || [];
  } catch (error) {
    console.error('Error fetching available contacts:', error);
    throw error;
  }
};

export const getMessages = async (token, baseURL, conversationId, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_messages&conversationid=${conversationId}&courseid=${courseId}&moodlewsrestformat=json`;
    const response = await axios.get(withCacheBuster(url));
    
    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }
    
    return response.data.messages || [];
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

export const sendMessage = async (token, baseURL, touserid, text, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_send_message&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('touserid', touserid);
    params.append('text', text);
    params.append('courseid', courseId);

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

// Marks a conversation as read for the current user. Course-scoped: the
// backend guard rejects a conversation that does not belong to courseId.
export const markRead = async (token, baseURL, conversationId, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_mark_read&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('conversationid', conversationId);
    params.append('courseid', courseId);

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

// Read-side role gate: canbroadcast is server-authoritative, never inferred
// client-side. Also returns the recipient roster for the composer.
export const getBroadcastRecipients = async (token, baseURL, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_broadcast_recipients&moodlewsrestformat=json&courseid=${courseId}`;
    const response = await axios.get(withCacheBuster(url));

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching broadcast recipients:', error);
    throw error;
  }
};

export const sendBroadcastMessage = async (token, baseURL, courseId, touserids, text) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_send_broadcast&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('courseid', courseId);
    touserids.forEach((id, index) => {
      params.append(`touserids[${index}]`, id);
    });
    params.append('text', text);

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error sending broadcast message:', error);
    throw error;
  }
};

// Read-side role gate for teacher-created, bidirectional group chats.
// cancreategroup is server-authoritative, same pattern as canbroadcast.
export const getGroupCandidates = async (token, baseURL, courseId) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_group_candidates&moodlewsrestformat=json&courseid=${courseId}`;
    const response = await axios.get(withCacheBuster(url));

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching group candidates:', error);
    throw error;
  }
};

export const createGroup = async (token, baseURL, courseId, name, userids) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_create_group&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('courseid', courseId);
    params.append('name', name);
    userids.forEach((id, index) => {
      params.append(`userids[${index}]`, id);
    });

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
};

export const addGroupMembers = async (token, baseURL, conversationId, userids) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_add_group_members&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('conversationid', conversationId);
    userids.forEach((id, index) => {
      params.append(`userids[${index}]`, id);
    });

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error adding group members:', error);
    throw error;
  }
};

export const removeGroupMembers = async (token, baseURL, conversationId, userids) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_remove_group_members&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('conversationid', conversationId);
    userids.forEach((id, index) => {
      params.append(`userids[${index}]`, id);
    });

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error removing group members:', error);
    throw error;
  }
};

export const renameGroup = async (token, baseURL, conversationId, name) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_rename_group&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('conversationid', conversationId);
    params.append('name', name);

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error renaming group:', error);
    throw error;
  }
};

// Bidirectional group send — distinct from sendBroadcastMessage (read-only
// announcement thread) and sendMessage (1:1 DM). Any current member may post.
export const sendGroupMessage = async (token, baseURL, conversationId, text) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_send_group_message&moodlewsrestformat=json`;

    const params = new URLSearchParams();
    params.append('conversationid', conversationId);
    params.append('text', text);

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return response.data;
  } catch (error) {
    console.error('Error sending group message:', error);
    throw error;
  }
};

export const getUnreadSummary = async (token, baseURL) => {
  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_get_unread_summary&moodlewsrestformat=json`;
    const response = await axios.get(withCacheBuster(url));

    if (response.data && response.data.errorcode) {
      throw new Error(response.data.message || response.data.errorcode);
    }

    return {
      totalunread: response.data.totalunread || 0,
      courses: response.data.courses || [],
    };
  } catch (error) {
    console.error('Error fetching unread summary:', error);
    throw error;
  }
};

const OFFLINE_ERROR_QUEUE_KEY = 'tipddy_offline_error_queue_v1';
const MAX_OFFLINE_QUEUE_SIZE = 20;

export const getOfflineErrorQueue = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(OFFLINE_ERROR_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveOfflineErrorQueue = (queue) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(OFFLINE_ERROR_QUEUE_KEY, JSON.stringify(queue.slice(-MAX_OFFLINE_QUEUE_SIZE)));
  } catch (e) {
    // Ignore storage quota or disabled localStorage errors
  }
};

export const flushOfflineErrors = async () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const queue = getOfflineErrorQueue();
  if (!queue || !queue.length) return;

  const remaining = [];
  for (const item of queue) {
    try {
      const url = `${item.baseURL}/webservice/rest/server.php?wstoken=${item.token}&wsfunction=local_tipddy_messenger_report_client_error&moodlewsrestformat=json`;
      const params = new URLSearchParams();
      params.append('courseid', item.courseId);
      params.append('action', `${item.action}_offline_recovered`);
      params.append('errormessage', item.errorMessage);

      await axios.post(url, params.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    } catch (err) {
      // If it failed because network is still down (no HTTP response), keep it in the queue
      if (!err.response) {
        remaining.push(item);
      }
      // If server responded with an error (e.g. 4xx/5xx), discard to prevent infinite retry loops
    }
  }

  saveOfflineErrorQueue(remaining);
};

// Listen for reconnection in browser environments
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushOfflineErrors().catch(() => {});
  });
}

export const reportClientError = async (token, baseURL, courseId, action, errorMessage) => {
  if (!token || !baseURL || !courseId) return;

  const formattedMsg = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage);

  // If navigator reports offline, enqueue immediately without attempting HTTP request
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const queue = getOfflineErrorQueue();
    queue.push({
      token,
      baseURL,
      courseId,
      action,
      errorMessage: formattedMsg,
      timestamp: Date.now(),
    });
    saveOfflineErrorQueue(queue);
    return;
  }

  try {
    const url = `${baseURL}/webservice/rest/server.php?wstoken=${token}&wsfunction=local_tipddy_messenger_report_client_error&moodlewsrestformat=json`;
    const params = new URLSearchParams();
    params.append('courseid', courseId);
    params.append('action', action);
    params.append('errormessage', formattedMsg);

    await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    // Opportunistically flush any queued errors from previous offline periods
    flushOfflineErrors().catch(() => {});
  } catch (err) {
    // If request failed because network is down, enqueue for retry when online
    if (!err.response) {
      const queue = getOfflineErrorQueue();
      queue.push({
        token,
        baseURL,
        courseId,
        action,
        errorMessage: formattedMsg,
        timestamp: Date.now(),
      });
      saveOfflineErrorQueue(queue);
    }
    console.warn('Failed to report client error to server (queued if offline):', err);
  }
};


